type AudioSession = EventTarget & {
  type: string;
  state?: string;
};

type OutputVideo = HTMLVideoElement & {
  sinkId?: string;
  setSinkId?: (deviceId: string) => Promise<void>;
};

type Playback = {
  play: () => void | Promise<void>;
  isPaused: () => boolean;
};

/** Keep the customer's continuous live playback on the system-selected output.
 * No microphone permission or device enumeration is needed for playback.
 */
export function createViewerAudioRecovery(
  player: Playback,
  video: OutputVideo,
  onNeedsGesture: () => void,
) {
  const devices = navigator.mediaDevices;
  const session = (navigator as Navigator & { audioSession?: AudioSession }).audioSession;
  const previousSessionType = session?.type;
  let ownsSessionType = false;
  try {
    if (session && session.type !== "playback") {
      session.type = "playback";
      ownsSessionType = true;
    }
  } catch { /* AudioSession is optional, including in embedded browsers. */ }

  let disposed = false;
  let started = false;
  let suspended = false;
  let blocked = false;
  let pending = false;
  let recovering = false;
  let interrupted = false;
  let timer: number | undefined;
  let verification: number | undefined;
  let generation = 0;
  let attempts: number[] = [];

  const clearTimers = () => {
    window.clearTimeout(timer);
    window.clearTimeout(verification);
    timer = verification = undefined;
  };
  const cancel = () => {
    generation += 1;
    pending = recovering = false;
    clearTimers();
  };
  const canRecover = () => !disposed && started && !suspended && !blocked
    && !document.hidden && !video.ended && session?.state !== "interrupted";
  const needsGesture = () => {
    if (disposed) return;
    blocked = true;
    cancel();
    onNeedsGesture();
  };

  const recover = async () => {
    timer = undefined;
    if (!canRecover() || !pending) return;
    window.clearTimeout(verification);
    verification = undefined;
    pending = false;
    recovering = true;
    const current = ++generation;
    const isCurrent = () => current === generation && canRecover();
    try {
      // An empty sink follows the browser/OS route (AirPods or built-in speaker).
      // Do not select a named device: that would pin playback to a disconnected sink.
      if (video.setSinkId && video.sinkId) {
        try { await video.setSinkId(""); } catch { /* Keep the browser's default routing. */ }
      }
      if (!isCurrent()) return;
      if (!video.paused && !player.isPaused()) {
        recovering = false;
        return;
      }
      attempts = attempts.filter(at => Date.now() - at < 5000);
      if (attempts.length >= 2) {
        needsGesture();
        return;
      }
      attempts.push(Date.now());
      // Leave volume and mute untouched, including the browser's autoplay mute.
      await player.play();
      if (!isCurrent()) return;
      verification = window.setTimeout(() => {
        verification = undefined;
        if (!isCurrent()) return;
        if (video.paused || player.isPaused()) needsGesture();
        else recovering = false;
      }, 1500);
    } catch {
      if (isCurrent()) needsGesture();
    }
  };

  const schedule = () => {
    if (disposed || !started || suspended || blocked || video.ended) return;
    pending = true;
    window.clearTimeout(timer);
    // Allow the OS to finish switching the Bluetooth route before resuming.
    if (canRecover()) timer = window.setTimeout(() => { void recover(); }, 350);
  };
  const playing = () => {
    if (disposed) return;
    cancel();
    started = true;
    suspended = blocked = false;
  };
  const onPause = () => {
    // iOS can pause the element without exposing a devicechange event. This
    // customer live view has no pause control. If Bluetooth settings hid the
    // page, defer this recovery until the customer returns to the live view.
    schedule();
  };
  const onSessionChange = () => {
    if (session?.state === "interrupted") {
      interrupted = started && !suspended && !blocked;
      cancel();
    } else if (interrupted) {
      interrupted = false;
      schedule();
    }
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      const shouldResume = pending || recovering;
      cancel();
      pending = shouldResume;
    } else if (pending) schedule();
  };
  const onFocus = () => { if (pending) schedule(); };

  devices?.addEventListener?.("devicechange", schedule);
  session?.addEventListener?.("statechange", onSessionChange);
  video.addEventListener("pause", onPause);
  video.addEventListener("playing", playing);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("focus", onFocus);

  return {
    playing,
    playbackBlocked: () => {
      // IVS can report AUDIO_BLOCKED from the native pause itself, before our
      // pause listener runs. A previously playing stream gets one recovery;
      // a blocked recovery must keep the gesture button, never mute-and-loop.
      if (!started) {
        blocked = true;
        cancel();
        return false;
      }
      if (recovering) { blocked = true; cancel(); }
      else schedule();
      return true;
    },
    canReconnect: () => !disposed && !blocked && !pending && !recovering
      && session?.state !== "interrupted",
    block: () => { blocked = true; cancel(); },
    suspend: () => { suspended = true; interrupted = false; cancel(); },
    dispose: () => {
      disposed = true;
      cancel();
      devices?.removeEventListener?.("devicechange", schedule);
      session?.removeEventListener?.("statechange", onSessionChange);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("playing", playing);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      try {
        if (ownsSessionType && session?.type === "playback" && previousSessionType) {
          session.type = previousSessionType;
        }
      } catch { /* The session may have been released by the browser. */ }
    },
  };
}
